/**
 * Onboarding · Prerequisites.
 *
 * Setup-checklist gate shown before the Strategy onboarding chat. Calls the
 * `Marketing: Check Onboarding Prerequisites` action with the current CompanyID,
 * renders the returned checklist grouped by `group`, each item with a status
 * pill + label + detail. `manual` items (DKIM, email-warmed) get an attestation
 * checkbox.
 *
 * v1 semantics:
 *  - Manual attestations are EPHEMERAL — in-component state only, not persisted.
 *    // TODO(prereq-v1): persist attestations + decide hard-gate vs soft-warn
 *  - "Continue to Strategy chat" is SOFT-WARN: always enabled regardless of
 *    fail/unknown items. A non-blocking banner appears above it when items fail.
 *    The hard-gate-vs-soft-warn decision is still open.
 */
import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { PillVariant } from '../../shared/pill.component';

type ChecklistStatus = 'pass' | 'fail' | 'warn' | 'manual' | 'unknown';

/** One row of the checklist as returned by the backend action's `Checklist` output. */
interface ChecklistItem {
    key: string;
    label: string;
    group: string;
    status: ChecklistStatus;
    detail: string;
    autoDetected: boolean;
}

/** Items bucketed under a single `group` header for rendering. */
interface ChecklistGroup {
    name: string;
    items: ChecklistItem[];
}

/** Shape of each element in RunAction's ResultData array. */
interface ActionOutputParam {
    Name: string;
    Type: string;
    Value: unknown;
}

const ACTION_NAME = 'Marketing: Check Onboarding Prerequisites';
const VALID_STATUSES: ReadonlySet<string> = new Set(['pass', 'fail', 'warn', 'manual', 'unknown']);

function isRecord(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isActionOutputParam(v: unknown): v is ActionOutputParam {
    return isRecord(v) && typeof v['Name'] === 'string' && typeof v['Type'] === 'string';
}

/**
 * RunAction's `ResultData` is the output-params collection. The server's
 * `CopyScalarsAndArrays` serialization means it can arrive EITHER as a JSON
 * array `[{Name,Type,Value},…]` OR as an array-like object with numeric string
 * keys `{"0":{…},"1":{…}}` (observed in production). Normalize both to an array
 * before scanning, then find a named output param's value.
 */
function getActionOutput(resultDataJson: string | null | undefined, name: string): unknown {
    if (!resultDataJson) return undefined;
    let parsed: unknown;
    try {
        parsed = JSON.parse(resultDataJson);
    } catch {
        return undefined;
    }
    const items: unknown[] = Array.isArray(parsed)
        ? parsed
        : (isRecord(parsed) ? Object.values(parsed) : []);
    for (const item of items) {
        if (isActionOutputParam(item) && item.Name === name) return item.Value;
    }
    return undefined;
}

/** Coerce one raw checklist element into a typed ChecklistItem, or null if malformed. */
function toChecklistItem(raw: unknown): ChecklistItem | null {
    if (!isRecord(raw)) return null;
    const key = raw['key'];
    const label = raw['label'];
    const group = raw['group'];
    const status = raw['status'];
    if (typeof key !== 'string' || typeof label !== 'string') return null;
    const statusStr = typeof status === 'string' && VALID_STATUSES.has(status) ? (status as ChecklistStatus) : 'unknown';
    return {
        key,
        label,
        group: typeof group === 'string' && group.length > 0 ? group : 'Other',
        status: statusStr,
        detail: typeof raw['detail'] === 'string' ? raw['detail'] : '',
        autoDetected: raw['autoDetected'] === true,
    };
}

@Component({
    standalone: false,
    selector: 'mkt-ws-onboarding-prerequisites',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Onboarding · Prerequisites' + (companyName ? ' · ' + companyName : '')"
    title="Before you start."
    sub="A quick readiness check — tracking, deliverability, CRM, publishing. We auto-detect what we can from your domain; tick the items only you can confirm. Nothing here blocks you in v1.">
</mkt-ws-page-head>

<div class="pre__toolbar">
    <button type="button" class="pre__btn pre__btn--recheck" [disabled]="loading" (click)="recheck()">
        <i class="fa-solid" [class.fa-rotate]="!loading" [class.fa-spinner]="loading" [class.fa-spin]="loading"></i>
        Re-check
    </button>
    <span *ngIf="domainEcho" class="pre__domain">Domain: <strong>{{ domainEcho }}</strong></span>
</div>

<div *ngIf="loading" class="pre__loading"><i class="fa-solid fa-spinner fa-spin"></i> Running checks…</div>

<div *ngIf="!loading && errorMessage" class="pre__error">
    <i class="fa-solid fa-triangle-exclamation"></i>
    <span>{{ errorMessage }}</span>
</div>

<ng-container *ngIf="!loading && !errorMessage">
    <!-- Summary KPIs -->
    <div class="pre__kpis">
        <div class="pre__kpi pre__kpi--pass"><div class="pre__kpi-value">{{ passCount }}</div><div class="pre__kpi-label">Passing</div></div>
        <div class="pre__kpi" [class.pre__kpi--alert]="failCount > 0"><div class="pre__kpi-value">{{ failCount }}</div><div class="pre__kpi-label">Failing</div></div>
        <div class="pre__kpi"><div class="pre__kpi-value">{{ warnCount }}</div><div class="pre__kpi-label">Warnings</div></div>
        <div class="pre__kpi"><div class="pre__kpi-value">{{ manualCount }}</div><div class="pre__kpi-label">To confirm</div></div>
    </div>

    <mkt-ws-coming-soon
        *ngIf="groups.length === 0"
        icon="fa-clipboard-list"
        title="No checklist items"
        body="The prerequisites action returned an empty checklist. Try Re-check, or confirm the action is configured for this company.">
    </mkt-ws-coming-soon>

    <!-- Grouped checklist -->
    <div *ngFor="let g of groups" class="pre__group">
        <div class="pre__group-head">{{ g.name }}</div>
        <div class="pre__list">
            <div *ngFor="let item of g.items" class="pre__row" [ngClass]="'pre__row--' + item.status">
                <mkt-ws-pill [variant]="statusVariant(item.status)">{{ statusLabel(item.status) }}</mkt-ws-pill>
                <div class="pre__body">
                    <div class="pre__label">
                        {{ item.label }}
                        <i *ngIf="item.autoDetected" class="fa-solid fa-wand-magic-sparkles pre__auto" title="Auto-detected"></i>
                    </div>
                    <div *ngIf="item.detail" class="pre__detail">{{ item.detail }}</div>
                </div>
                <label *ngIf="item.status === 'manual'" class="pre__attest">
                    <input type="checkbox" [checked]="isAttested(item.key)" (change)="toggleAttest(item.key)" />
                    <span>I confirm</span>
                </label>
            </div>
        </div>
    </div>

    <!-- Soft-warn banner — never blocks Continue in v1 -->
    <div *ngIf="failCount > 0" class="pre__warn-banner">
        <i class="fa-solid fa-circle-exclamation"></i>
        <span>{{ failCount }} prerequisite{{ failCount === 1 ? '' : 's' }} {{ failCount === 1 ? 'is' : 'are' }} failing. You can still continue — the agent will work with what's available, but quality suffers without these.</span>
    </div>

    <div class="pre__footer">
        <button type="button" class="pre__btn pre__btn--continue" (click)="continueToChat()">
            Continue to Strategy chat <i class="fa-solid fa-arrow-right"></i>
        </button>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }

        .pre__toolbar { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
        .pre__domain { font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }

        .pre__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .pre__error { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-radius: 10px; background: var(--mj-color-error-50, #fff1f2); border: 1px solid var(--mj-color-error-100, #fecdd3); color: var(--mj-status-error, #be123c); font-size: 13px; }

        .pre__kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .pre__kpi { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 14px 16px; }
        .pre__kpi--pass .pre__kpi-value { color: var(--mj-status-success, #16a34a); }
        .pre__kpi--alert .pre__kpi-value { color: var(--mj-status-error, #be123c); }
        .pre__kpi-value { font-size: 26px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); font-variant-numeric: tabular-nums; line-height: 1.1; }
        .pre__kpi-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-top: 6px; }

        .pre__group { margin-bottom: 20px; }
        .pre__group-head { font-size: 11px; letter-spacing: 0.07em; text-transform: uppercase; font-weight: 700; color: var(--mj-text-secondary, #71717a); margin: 0 0 8px 2px; }
        .pre__list { display: flex; flex-direction: column; gap: 8px; }
        .pre__row { display: flex; align-items: flex-start; gap: 12px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 12px 14px; }
        .pre__row--fail { border-color: var(--mj-color-error-100, #fecdd3); }
        .pre__row > mkt-ws-pill { flex-shrink: 0; margin-top: 1px; }
        .pre__body { flex: 1; min-width: 0; }
        .pre__label { font-size: 13.5px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); display: flex; align-items: center; gap: 8px; }
        .pre__auto { font-size: 10.5px; color: var(--mj-brand-primary, #6366f1); }
        .pre__detail { font-size: 12px; line-height: 1.5; color: var(--mj-text-secondary, #71717a); margin-top: 4px; white-space: pre-wrap; }
        .pre__attest { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); cursor: pointer; user-select: none; margin-top: 1px; }
        .pre__attest input { cursor: pointer; }

        .pre__warn-banner { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 10px; background: var(--mj-color-warning-50, #fffbeb); border: 1px solid var(--mj-color-warning-100, #fde68a); color: var(--mj-status-warning, #b45309); font-size: 12.5px; line-height: 1.5; margin-bottom: 16px; }

        .pre__footer { display: flex; justify-content: flex-end; padding-top: 4px; }

        .pre__btn { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; font-family: inherit; cursor: pointer; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); }
        .pre__btn:disabled { opacity: 0.5; cursor: default; }
        .pre__btn--recheck:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); }
        .pre__btn--continue { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .pre__btn--continue:hover { filter: brightness(1.08); }

        @media (max-width: 900px) { .pre__kpis { grid-template-columns: repeat(2, 1fr); } }
    `]
})
export class OnboardingPrerequisitesSection {
    @Input() companyId = '';
    @Input() companyName = '';
    /** Optional sending/website domain to pass to the action; the action also echoes one back. */
    @Input() domain = '';
    /** Workspace navigation callback — routes within the SPA without a full reload. */
    @Input() navigate: (path: string) => void = () => {};
    // Declared so the workspace's blanket section-prop binding doesn't NG0303.
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};
    @Input() userRole: string | null = null;

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    errorMessage = '';
    /** Echoed back from the action; shown in the toolbar. */
    domainEcho = '';
    groups: ChecklistGroup[] = [];

    /** Ephemeral manual attestations — keyed by item.key. NOT persisted (v1).
     *  // TODO(prereq-v1): persist attestations + decide hard-gate vs soft-warn */
    private attested = new Set<string>();

    /** Resolved Action.ID, cached after first lookup. */
    private actionId: string | null = null;
    /** Avoid re-running the check on every Angular CD pass — only on first view + Re-check. */
    private hasRun = false;

    private get items(): ChecklistItem[] {
        return this.groups.flatMap(g => g.items);
    }

    get passCount(): number { return this.items.filter(i => i.status === 'pass').length; }
    get failCount(): number { return this.items.filter(i => i.status === 'fail').length; }
    get warnCount(): number { return this.items.filter(i => i.status === 'warn').length; }
    get manualCount(): number { return this.items.filter(i => i.status === 'manual').length; }

    ngOnChanges(): void {
        // Run the check the first time we have a CompanyID. The shell may bind
        // companyId asynchronously, so guard on hasRun rather than ngOnInit.
        if (!this.hasRun && this.companyId) {
            this.hasRun = true;
            void this.runCheck();
        }
    }

    recheck(): void {
        if (this.loading) return;
        void this.runCheck();
    }

    isAttested(key: string): boolean {
        return this.attested.has(key);
    }

    toggleAttest(key: string): void {
        if (this.attested.has(key)) this.attested.delete(key);
        else this.attested.add(key);
        this.cdr.detectChanges();
    }

    continueToChat(): void {
        // v1 = soft-warn: always navigate, regardless of failing/unknown items.
        this.navigate('onboarding/chat');
    }

    statusVariant(status: ChecklistStatus): PillVariant {
        switch (status) {
            case 'pass': return 'good';
            case 'fail': return 'bad';
            case 'warn': return 'warn';
            case 'manual': return 'info';
            default: return 'muted';
        }
    }

    statusLabel(status: ChecklistStatus): string {
        switch (status) {
            case 'pass': return 'Pass';
            case 'fail': return 'Fail';
            case 'warn': return 'Warn';
            case 'manual': return 'Confirm';
            default: return 'Unknown';
        }
    }

    /**
     * Call the backend action, parse `ResultData` for the `Checklist` + `Domain`
     * outputs, and rebuild the grouped view. On any failure we surface an error
     * (and LogError) rather than silently render empty.
     */
    private async runCheck(): Promise<void> {
        if (!this.companyId) {
            this.errorMessage = 'No company selected — cannot run prerequisite checks.';
            this.cdr.detectChanges();
            return;
        }
        this.loading = true;
        this.errorMessage = '';
        this.cdr.detectChanges();

        try {
            const actionId = await this.resolveActionId();
            if (!actionId) {
                this.errorMessage = `Could not find action "${ACTION_NAME}". Has metadata been synced?`;
                return;
            }

            const gql = Metadata.Provider as GraphQLDataProvider;
            const params: { Name: string; Value: string; Type: string }[] = [
                { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
            ];
            if (this.domain) params.push({ Name: 'Domain', Value: this.domain, Type: 'Input' });

            const result = await gql.ExecuteGQL(
                `mutation RunAction($input: RunActionInput!) {
                    RunAction(input: $input) { Success Message ResultCode ResultData }
                }`,
                { input: { ActionID: actionId, Params: params } }
            );

            const response = result?.RunAction;
            if (!response?.Success) {
                const msg = response?.Message || 'Prerequisite check action failed.';
                LogError(`[OnboardingPrerequisites] action returned failure: ${msg} (code: ${response?.ResultCode ?? 'UNKNOWN'})`);
                this.errorMessage = msg;
                return;
            }

            const resultData: string | null = typeof response.ResultData === 'string' ? response.ResultData : null;

            // Domain echo (best-effort — not fatal if absent).
            const domainOut = getActionOutput(resultData, 'ResolvedDomain');
            this.domainEcho = typeof domainOut === 'string' ? domainOut : (this.domain || '');

            // Checklist — its Value may be a JSON string or an already-parsed array.
            const checklistVal = getActionOutput(resultData, 'Checklist');
            if (checklistVal === undefined) {
                LogError(`[OnboardingPrerequisites] ResultData had no 'Checklist' output param. ResultData=${resultData ?? '(null)'}`);
                this.errorMessage = 'Prerequisite check returned no checklist data.';
                return;
            }

            let checklistArr: unknown;
            if (typeof checklistVal === 'string') {
                try {
                    checklistArr = JSON.parse(checklistVal);
                } catch (parseErr) {
                    const m = parseErr instanceof Error ? parseErr.message : String(parseErr);
                    LogError(`[OnboardingPrerequisites] could not JSON-parse Checklist value: ${m}`);
                    this.errorMessage = 'Could not parse the prerequisite checklist.';
                    return;
                }
            } else {
                checklistArr = checklistVal;
            }

            if (!Array.isArray(checklistArr)) {
                LogError(`[OnboardingPrerequisites] Checklist value was not an array (got ${typeof checklistArr}).`);
                this.errorMessage = 'Prerequisite checklist was not in the expected format.';
                return;
            }

            this.groups = this.buildGroups(checklistArr);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[OnboardingPrerequisites] runCheck threw: ${msg}`);
            this.errorMessage = msg;
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    /** Bucket valid checklist items by `group`, preserving first-seen group order. */
    private buildGroups(rawItems: unknown[]): ChecklistGroup[] {
        const byGroup = new Map<string, ChecklistGroup>();
        for (const raw of rawItems) {
            const item = toChecklistItem(raw);
            if (!item) continue;
            let group = byGroup.get(item.group);
            if (!group) {
                group = { name: item.group, items: [] };
                byGroup.set(item.group, group);
            }
            group.items.push(item);
        }
        return [...byGroup.values()];
    }

    private async resolveActionId(): Promise<string | null> {
        if (this.actionId) return this.actionId;
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name='${ACTION_NAME.replace(/'/g, "''")}'`,
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        if (result.Success && result.Results.length > 0) {
            this.actionId = result.Results[0].ID;
            return this.actionId;
        }
        const reason = result.Success ? 'no rows returned' : (result.ErrorMessage || 'query failed');
        LogError(`[OnboardingPrerequisites] resolveActionId failed for '${ACTION_NAME}': ${reason}`);
        return null;
    }
}
